import { AxiosInstance } from "axios";
import QueryString from "qs";
import { ApiRouteHelpers as Helpers } from "../../../root/apiRoutes";
import { OkReturnDto } from "../dto/OkReturnDto";
import {
  AddOrUpdateCustomVendorDto,
  CollectionContext,
  OrganizationDataRecipientDto,
  OrganizationDataRecipientPlatformDto,
  OrganizationVendorNewAndEditDto,
  OrganizationVendorSettingsDto,
  YotpoMigrationRequestDto,
} from "../dto/OrganizationDataRecipientDto";
import { VendorDto } from "../dto/VendorDto";
import {
  MappingProgressEnum,
  OrganizationPublicId,
  Page,
  UUIDString,
  VendorPublicId,
} from "../Types";
import { AutomationVendorTaskDto } from "../dto/AutomationVendorTaskDto";

export class OrganizationVendorController {
  constructor(private axios: AxiosInstance) {}

  public getAutomationHistory = async (
    organizationId: OrganizationPublicId,
    page: number = 0,
    size: number = 20,
    failedOnly: boolean = false,
  ): Promise<Page<AutomationVendorTaskDto>> => {
    const url = Helpers.organizationVendorAutomationHistory(organizationId);
    const response = await this.axios.get<Page<AutomationVendorTaskDto>>(url, {
      params: { page, size, sort: "createdAt,desc", failedOnly },
    });
    return response.data;
  };

  public getVendors = async (
    organizationId: OrganizationPublicId,
    excludeCategories?: String[],
    includeDataStorageLocations: boolean = false,
    excludeIncomplete: boolean | undefined = undefined,
  ): Promise<OrganizationDataRecipientDto[]> => {
    const url = Helpers.organizationVendors(organizationId);
    const response = await this.axios.get<OrganizationDataRecipientDto[]>(url, {
      params: { excludeCategories, excludeIncomplete, includeDataStorageLocations },
      paramsSerializer: (q) => QueryString.stringify(q, { arrayFormat: "comma" }),
    });

    return response.data as OrganizationDataRecipientDto[];
  };

  public getUnmappedVendors = async (
    organizationId: OrganizationPublicId,
    collectionGroupId: UUIDString,
  ): Promise<OrganizationDataRecipientDto[]> => {
    const url = Helpers.organizationUnmappedVendors(organizationId, collectionGroupId);
    const response = await this.axios.get<OrganizationDataRecipientDto[]>(url, {});

    return response.data as OrganizationDataRecipientDto[];
  };

  public getVendorCatalog = async (organizationId: OrganizationPublicId): Promise<VendorDto[]> => {
    const commonCatalogUrl = Helpers.organizationVendorCommonCatalog(organizationId);
    const customCatalogUrl = Helpers.organizationVendorCustomCatalog(organizationId);
    const [commonCatalogResponse, customCatalogResponse] = await Promise.all([
      this.axios.get<VendorDto[]>(commonCatalogUrl, {}),
      this.axios.get<VendorDto[]>(customCatalogUrl, {}),
    ]);
    return [
      ...(commonCatalogResponse.data as VendorDto[]),
      ...(customCatalogResponse.data as VendorDto[]),
    ];
  };

  public getVendorDetails = async (
    organizationId: OrganizationPublicId,
    vendorId: UUIDString,
  ): Promise<OrganizationDataRecipientDto> => {
    const url = Helpers.organizationVendor(organizationId, vendorId);
    const response = await this.axios.get<OrganizationDataRecipientDto>(url, {});

    return response.data;
  };

  public getPlatformApps = async (
    organizationId: OrganizationPublicId,
    platformVendorId: VendorPublicId,
  ): Promise<OrganizationDataRecipientPlatformDto> => {
    const url = Helpers.organizationPlatformApps(organizationId, platformVendorId);
    const response = await this.axios.get<OrganizationDataRecipientPlatformDto>(url, {});

    return response.data;
  };

  public getPlatformAppsByVendorKey = async (
    organizationId: OrganizationPublicId,
    vendorKey: string,
  ): Promise<OrganizationDataRecipientPlatformDto | null> => {
    const url = Helpers.organizationPlatformAppsByVendorKey(organizationId, vendorKey);
    try {
      const response = await this.axios.get<OrganizationDataRecipientPlatformDto>(url, {});
      return response.status == 200 ? response.data : null;
    } catch (e) {
      return null;
    }
  };

  public getAppPlatforms = async (
    organizationId: OrganizationPublicId,
    platformVendorId: VendorPublicId,
  ): Promise<OrganizationDataRecipientPlatformDto> => {
    const url = Helpers.organizationAppPlatforms(organizationId, platformVendorId);
    const response = await this.axios.get<OrganizationDataRecipientPlatformDto>(url, {});

    return response.data;
  };

  public getNewData = async (
    organizationId: OrganizationPublicId,
  ): Promise<OrganizationVendorNewAndEditDto> => {
    const url = Helpers.organization.vendors.new(organizationId);
    const response = await this.axios.get<OrganizationVendorNewAndEditDto>(url, {});
    return response.data as OrganizationVendorNewAndEditDto;
  };

  public getEditData = async (
    organizationId: OrganizationPublicId,
    vendorId: UUIDString,
  ): Promise<OrganizationVendorNewAndEditDto> => {
    const url = Helpers.organization.vendors.edit(organizationId, vendorId);
    const response = await this.axios.get<OrganizationVendorNewAndEditDto>(url, {});
    return response.data as OrganizationVendorNewAndEditDto;
  };

  public getAddData = async (
    organizationId: OrganizationPublicId,
    vendorId: UUIDString,
  ): Promise<OrganizationVendorNewAndEditDto> => {
    const url = Helpers.organization.vendors.add(organizationId, vendorId);
    const response = await this.axios.get<OrganizationVendorNewAndEditDto>(url, {});
    return response.data as OrganizationVendorNewAndEditDto;
  };

  public searchVendors = async (
    organizationId: OrganizationPublicId,
    vendorName: string,
  ): Promise<VendorDto[]> => {
    const url = `${Helpers.organization.vendors.search(organizationId)}?name=${vendorName}`;
    const response = await this.axios.get<VendorDto[]>(url, {});
    return response.data as VendorDto[];
  };

  public addDefaultVendors = async (organizationId: OrganizationPublicId): Promise<void> => {
    const url = Helpers.organizationVendorsDefault(organizationId);
    await this.axios.post(url);
  };

  public addOrUpdateVendor = async (
    organizationId: OrganizationPublicId,
    vendorId: UUIDString,
    organizationVendorSettings: OrganizationVendorSettingsDto,
  ): Promise<OrganizationDataRecipientDto> => {
    const url = Helpers.organizationVendor(organizationId, vendorId);
    const response = await this.axios.put<OrganizationDataRecipientDto>(
      url,
      organizationVendorSettings,
    );
    return response.data as OrganizationDataRecipientDto;
  };

  public addCustomVendor = async (
    organizationId: OrganizationPublicId,
    organizationVendorDto: AddOrUpdateCustomVendorDto,
  ): Promise<OrganizationDataRecipientDto> => {
    const url = Helpers.organization.vendors.root(organizationId);
    const response = await this.axios.post<OrganizationDataRecipientDto>(
      url,
      organizationVendorDto,
    );
    return response.data as OrganizationDataRecipientDto;
  };

  public getThirdPartyDataRecipients = async (
    organizationId: OrganizationPublicId,
  ): Promise<VendorDto[]> => {
    const url = Helpers.organization.vendors.thirdPartyRecipients(organizationId);
    const response = await this.axios.get<VendorDto[]>(url, {});
    return response.data as VendorDto[];
  };

  public removeVendor = async (
    organizationId: OrganizationPublicId,
    vendorId: UUIDString,
    collectionContext?: CollectionContext[],
    deleteOnEmpty: boolean = true,
    removeFromDataMap: boolean = false,
    // With deleteOnEmpty: removing the last context deletes the recipient but keeps its data source.
    keepDataSource: boolean = false,
  ): Promise<Record<string, boolean>> => {
    const url = Helpers.organizationVendor(organizationId, vendorId);
    const response = await this.axios.delete<Record<string, boolean>>(url, {
      params: { collectionContext, deleteOnEmpty, removeFromDataMap, keepDataSource },
      paramsSerializer: (q) => QueryString.stringify(q, { arrayFormat: "comma" }),
    });
    return response.data as Record<string, boolean>;
  };

  public removeVendorAgreementFile = async (
    organizationId: OrganizationPublicId,
    vendorId: UUIDString,
  ): Promise<OrganizationDataRecipientDto> => {
    const url = Helpers.organizationVendorAgreement(organizationId, vendorId);
    const response = await this.axios.delete<OrganizationDataRecipientDto>(url);
    return response.data as OrganizationDataRecipientDto;
  };

  public addPlatformApplication = async (
    organizationId: string,
    platformId: string,
    appId: string,
  ): Promise<void> => {
    const url = Helpers.organizationPlatformInstall(organizationId, platformId, appId);
    await this.axios.post(url);
  };

  public removePlatformApplication = async (
    organizationId: string,
    platformId: string,
    appId: string,
  ): Promise<void> => {
    const url = Helpers.organizationPlatformInstall(organizationId, platformId, appId);
    await this.axios.delete(url);
  };

  public removeVendors = async (
    organizationId: OrganizationPublicId,
    vendors: UUIDString[],
  ): Promise<Record<string, boolean>> => {
    const url = Helpers.organizationRemoveVendors(organizationId);
    const response = await this.axios.post<Record<string, boolean>>(url, { vendors });
    return response.data as Record<string, boolean>;
  };

  public updateMappingProgress = async (
    organizationId: OrganizationPublicId,
    vendorId: UUIDString,
    mappingProgress: MappingProgressEnum,
  ): Promise<OkReturnDto> => {
    const url = Helpers.organizationVendorMappingProgress(
      organizationId,
      vendorId,
      mappingProgress,
    );
    const response = await this.axios.put<OkReturnDto>(url);
    return response.data;
  };

  public requestDocumentation = async (
    organizationId: OrganizationPublicId,
    vendors: UUIDString[],
    body?: string,
  ): Promise<OkReturnDto> => {
    const url = Helpers.organizationVendorRequestDocumentation(organizationId);
    const response = await this.axios.post<OkReturnDto>(url, {
      vendors,
      body,
    });
    return response.data;
  };

  public requestVendorDocumentationBulk = async (
    organizationId: OrganizationPublicId,
    requests: Array<{ vendorId: UUIDString; body: string }>,
  ): Promise<OkReturnDto> => {
    const url = Helpers.organizationRequestVendorDocumentationBulk(organizationId);
    const response = await this.axios.post<OkReturnDto>(url, { requests });
    return response.data;
  };

  public requestVendorServiceProviderInfo = async (
    organizationId: OrganizationPublicId,
    vendorIds: UUIDString[],
    vendorEmails: string[],
    body?: string,
    subject?: string,
  ): Promise<OkReturnDto> => {
    const url = Helpers.organizationRequestVendorServiceProviderInfo(organizationId);
    const response = await this.axios.post<OkReturnDto>(url, {
      vendorIds,
      vendorEmails,
      body,
      subject,
    });
    return response.data;
  };

  public requestGdprProcessorStatusInfo = async (
    organizationId: OrganizationPublicId,
    vendorIds: UUIDString[],
    vendorEmails: string[],
    body?: string,
  ): Promise<OkReturnDto> => {
    const url = Helpers.organizationRequestGdprProcessorStatusInfo(organizationId);
    const response = await this.axios.post<OkReturnDto>(url, {
      vendorIds,
      vendorEmails,
      body,
    });
    return response.data;
  };

  public migrateYotpo = async (
    organizationId: OrganizationPublicId,
    yotpoMigrationRequestDto: YotpoMigrationRequestDto,
  ): Promise<OkReturnDto> => {
    const url = Helpers.organization.vendors.migrateYotpo(organizationId);
    const response = await this.axios.post<OkReturnDto>(url, yotpoMigrationRequestDto);
    return response.data;
  };
}
